import { fetchApi } from '@/config/api'
import type { 
  VendeurPrestationApiResponse, 
  CommandesPrestationsApiData
} from '@/types/vendeurPrestationApi'
import type {ChiffreAffaireSeries, DashboardApiResponse, ProduitsEnPanierApi} from '@/types'

type TopReferenceResult = {
  id_produit: number
  reference: string | null
  imageUrl?: string | null
  categorie?: string | null
  quantite: number
  chiffre_affaires: number
}

export class CommandesPrestationsApi {
  
  /**
   * Récupère toutes les commandes d'un vendeur
   */
  static async getCommandesByVendeur(idVendeur: number): Promise<VendeurPrestationApiResponse[]> {
    const query = `
      query GetCommandesPrestations($idVendeur: Int!) {
        commandesPrestations(idVendeur: $idVendeur) {
          id_vendeur_prestation
          id_vendeur
          id_prestation
          quantite
          montant
          libelle
          date_creation
          date_facturation
          id_prestation_statut
          id_shooting
          prestation {
            id_prestation
            libelle
            description
            montant
          }
        }
      }
    `
    
    const response = await fetchApi<CommandesPrestationsApiData>(query, { idVendeur })
    return response.commandesPrestations
  }

  /**
   * Crée une commande directement avec statut 1
   */
  static async creerCommande(idVendeur: number, articles: Array<{id_prestation: number, quantite: number, montant: number, libelle: string}>): Promise<VendeurPrestationApiResponse[]> {
    const mutation = `
      mutation CreerCommandePrestation($input: CreateCommandePrestationInput!) {
        creerCommandePrestation(input: $input) {
          id_vendeur_prestation
          id_vendeur
          id_prestation
          quantite
          montant
          libelle
          date_creation
          date_facturation
          id_prestation_statut
          id_shooting
          prestation {
            id_prestation
            libelle
            description
            montant
          }
        }
      }
    `
    
    const response = await fetchApi<{ creerCommandePrestation: VendeurPrestationApiResponse[] }>(mutation, { 
      input: { 
        idVendeur, 
        articles 
      } 
    })
    return response.creerCommandePrestation
  }

  /**
   * Chiffre d'affaires pour un mois donné (offset = 0 : mois courant)
   */
  static async getChiffreAffaireMois(idVendeur: number, offset = 0): Promise<number> {
    const query = `
      query ($idVendeur: Int!, $offset: Int) {
        chiffreAffaireMois(id_vendeur: $idVendeur, offset: $offset)
      }
    `
    const response = await fetchApi<{ chiffreAffaireMois: number }>(query, { idVendeur, offset })
    return response.chiffreAffaireMois
  }

  /**
   * Nombre de commandes pour un mois donné (offset = 0 : mois courant)
   */
  static async getTotalCommandesMois(idVendeur: number, offset = 0): Promise<number> {
    const query = `
      query ($idVendeur: Int!, $offset: Int) {
        totalCommandesMois(id_vendeur: $idVendeur, offset: $offset)
      }
    `
    const response = await fetchApi<{ totalCommandesMois: number }>(query, { idVendeur, offset })
    return response.totalCommandesMois
  }

  /**
   * Commandes en attente (statut attente)
   */
  static async getCommandesEnAttente(idVendeur: number): Promise<number> {
    const query = `
      query ($idVendeur: Int!) {
        commandesEnAttente(id_vendeur: $idVendeur)
      }
    `
    const response = await fetchApi<{ commandesEnAttente: number }>(query, { idVendeur })
    return response.commandesEnAttente
  }

  static async getChiffreAffaireParJour(
    idVendeur: number,
    range: string,
    includePrevious = true,
  ): Promise<ChiffreAffaireSeries> {
    const query = `
      query ($idVendeur: Int!, $range: String, $includePrevious: Boolean) {
        chiffreAffaireParJour(
          id_vendeur: $idVendeur
          range: $range
          includePrevious: $includePrevious
        ) {
          current {
            jour
            total
          }
          previous {
            jour
            total
          }
        }
      }
    `
    const res = await fetchApi<{ chiffreAffaireParJour: ChiffreAffaireSeries }>(query, {
      idVendeur,
      range,
      includePrevious,
    })
    return res.chiffreAffaireParJour
  }

  static async getBestAcheteurs(
    idVendeur: number,
    limit = 8,
    range = 'mois'
  ): Promise<Array<{ id_acheteur: number; nom: string; montant_total: number; commandes: number }>> {
    const query = `
      query ($idVendeur: Int!, $limit: Int, $range: String) {
        bestAcheteursMois(id_vendeur: $idVendeur, limit: $limit, range: $range) {
          id_acheteur
          nom
          montant_total
          commandes
        }
      }
    `
    const res = await fetchApi<{ bestAcheteursMois: Array<{ id_acheteur: number; nom: string; montant_total: number; commandes: number }> }>(query, {
      idVendeur,
      limit,
      range,
    })
    return res.bestAcheteursMois
  }

  static async getBestReferences(
    idVendeur: number,
    limit = 10,
    range = 'mois'
  ): Promise<TopReferenceResult[]> {
    const query = `
      query ($idVendeur: Int!, $limit: Int, $range: String) {
        bestReferencesMois(id_vendeur: $idVendeur, limit: $limit, range: $range) {
          id_produit
          reference
          imageUrl
          categorie
          quantite
          chiffre_affaires
        }
      }
    `
    const res = await fetchApi<{ bestReferencesMois: TopReferenceResult[] }>(query, {
      idVendeur,
      limit,
      range,
    })
    return res.bestReferencesMois
  }

  static async getBestReferencesByCountry(
    idVendeur: number,
    idPays: number | null,
    range = 'mois',
    limit = 5,
    idsPays: number[] = [],
  ): Promise<TopReferenceResult[]> {
    const query = `
      query ($idVendeur: Int!, $idPays: Int, $idsPays: [Int!], $range: String, $limit: Int) {
        bestReferencesParPays(id_vendeur: $idVendeur, id_pays: $idPays, ids_pays: $idsPays, range: $range, limit: $limit) {
          id_produit
          reference
          imageUrl
          categorie
          quantite
          chiffre_affaires
        }
      }
    `
    const res = await fetchApi<{ bestReferencesParPays: TopReferenceResult[] }>(query, {
      idVendeur,
      idPays,
      idsPays,
      range,
      limit,
    })
    return res.bestReferencesParPays
  }

  static async getProduitsEnPanier(
    idVendeur: number,
    limit = 10,
  ): Promise<ProduitsEnPanierApi> {
    const query = `
      query ($idVendeur: Int!, $limit: Int) {
        produitsEnPanier(id_vendeur: $idVendeur, limit: $limit) {
          acheteurs
          montant
          commandeMini
          paniers {
            id_acheteur
            nom
            montant
            date
            pays
            produits {
              id_produit
              reference
              imageUrl
              categorie
              quantite
              rupture
            }
          }
        }
      }
    `
    const res = await fetchApi<{
      produitsEnPanier: ProduitsEnPanierApi
    }>(query, { idVendeur, limit })
    return res.produitsEnPanier ?? { acheteurs: 0, montant: 0, commandeMini: 0, paniers: [] }
  }

    static async getDashboard(
        idVendeur: number,
        range = 'jour',
        limitAcheteurs = 8,
        limitReferences = 10,
        periodKey = range,
    ): Promise<DashboardApiResponse> {
        const query = `
    query Dashboard(
      $idVendeur: Int!,
      $offsetNow: Int!,
      $offsetPrev: Int!,
      $range: String!,
      $includePrevious: Boolean!,
      $limitAcheteurs: Int,
      $limitReferences: Int
    ) {
      caCourant: chiffreAffaireMois(id_vendeur: $idVendeur, offset: $offsetNow)
      caPrecedent: chiffreAffaireMois(id_vendeur: $idVendeur, offset: $offsetPrev)

      commandesCourantes: totalCommandesMois(id_vendeur: $idVendeur, offset: $offsetNow)
      commandesPrecedentes: totalCommandesMois(id_vendeur: $idVendeur, offset: $offsetPrev)

      enAttente: commandesEnAttente(id_vendeur: $idVendeur)

      bestAcheteursMois(id_vendeur: $idVendeur, limit: $limitAcheteurs, range: $range) {
        id_acheteur
        nom
        montant_total
        commandes
      }

      bestReferencesMois(id_vendeur: $idVendeur, limit: $limitReferences, range: $range) {
        id_produit
        reference
        imageUrl
        categorie
        quantite
        chiffre_affaires
      }

      repartitionClientsParPays(id_vendeur: $idVendeur, range: $range) {
        id_pays
        pays
        clients
      }

      chiffreAffaireParJour(
        id_vendeur: $idVendeur,
        range: $range,
        includePrevious: $includePrevious
      ) {
        current {
          jour
          total
        }
        previous {
          jour
          total
        }
      }
    }
  `

        // Note on offsets:
        // - For monthly KPIs, business now requires seasonal comparison: month N vs same month N-1.
        //   That corresponds to an offset of 12 months for the "previous" period.
        // - For other ranges we keep the existing behavior (previous month) until
        //   dedicated endpoints are available for day/week/year KPI comparisons.
        const offsetPrevForRange = periodKey === 'mois' ? 12 : 1

        const res = await fetchApi<{
            caCourant: number
            caPrecedent: number
            commandesCourantes: number
            commandesPrecedentes: number
            enAttente: number
            bestAcheteursMois: DashboardApiResponse['topAcheteurs']
            bestReferencesMois: DashboardApiResponse['topReferences']
            repartitionClientsParPays: DashboardApiResponse['repartitionPays']
            chiffreAffaireParJour: ChiffreAffaireSeries
        }>(query, {
            idVendeur,
            offsetNow: 0,
            offsetPrev: offsetPrevForRange,
            range,
            includePrevious: true,
            limitAcheteurs,
            limitReferences,
        })

        return {
            caCourant: res.caCourant ?? 0,
            caPrecedent: res.caPrecedent ?? 0,
            commandesCourantes: res.commandesCourantes ?? 0,
            commandesPrecedentes: res.commandesPrecedentes ?? 0,
            enAttente: res.enAttente ?? 0,
            topAcheteurs: res.bestAcheteursMois ?? [],
            topReferences: res.bestReferencesMois ?? [],
            repartitionPays: res.repartitionClientsParPays ?? [],
            chartSeries: res.chiffreAffaireParJour,
        }
    }


}




// Export des méthodes pour une utilisation plus simple
export const commandesPrestationsApi = {
  getCommandes: CommandesPrestationsApi.getCommandesByVendeur,
  creerCommande: CommandesPrestationsApi.creerCommande,
  getChiffreAffaireMois: CommandesPrestationsApi.getChiffreAffaireMois,
  getTotalCommandesMois: CommandesPrestationsApi.getTotalCommandesMois,
  getCommandesEnAttente: CommandesPrestationsApi.getCommandesEnAttente,
  getChiffreAffaireParJour: CommandesPrestationsApi.getChiffreAffaireParJour,
  getBestAcheteurs: CommandesPrestationsApi.getBestAcheteurs,
  getBestReferences: CommandesPrestationsApi.getBestReferences,
  getBestReferencesByCountry: CommandesPrestationsApi.getBestReferencesByCountry,
    getDashboard: CommandesPrestationsApi.getDashboard,
}
